import React, { useEffect, useState } from "react";
import { useUserContext } from "@/contexts/UserContext";
import { API } from "@/lib/api";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useLocation, useSearch, useSearchParams } from "wouter";
import { UserRole, UserStatus } from "@boxem/core-api-schema";
import TextInput from "@/components/forms/TextInput/TextInput";
import Button from "@/components/forms/Button/Button";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import Flex from "@/components/Flex/Flex";
import Link from "@/components/Link/Link";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";

import styles from "./login.module.css";
import { getWFSAuthLink } from "@/api/wfs/auth";
import { navigatePreserveSearchParams } from "@/utils/navigationUtils";

type LoginForm = {
  email: string;
  password: string;
};

export default function LoginPage() {
  const { setUser } = useUserContext();
  const searchParams = new URLSearchParams(useSearch());
  const { refetchSubscription } = useSubscriptionContext();
  const [query] = useSearchParams();
  const [_, navigate] = useLocation();
  const { showNotification } = useNotificationContext();
  const [submitButtonLoading, setSubmitButtonLoading] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(false);
  const [emailErrorText, setEmailErrorText] = useState<string>("");
  const [passwordErrorText, setPasswordErrorText] = useState<string>("");
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: "",
    password: "",
  });

  const navigatePath = query.get("path");
  const mirrorToken = searchParams.get("mt"); // "mt" -> Mirror Token
  const autofillEmail = searchParams.get("autofillEmail"); // currently used when coming from accepting an assistant invite email

  const walmartCallBackUri = query.get("walmartCallbackUri") || query.get("walmartCallBackUri");
  const walmartClientType = query.get("clientType");

  //Validate all the inputs - used for validation on submit
  const validateInputs = () => {
    let valid = true;

    //Email rules
    if (loginForm.email === "") {
      setEmailErrorText("Email address is required.");
      valid = false;
    }
    if (!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(loginForm.email)) {
      setEmailErrorText("Invalid email address.");
      valid = false;
    }

    //Password rules
    if (loginForm.password === "") {
      setPasswordErrorText("Password is required.");
      valid = false
    }

    //If the form was invalid then disable the submit button.
    if (!valid) {
      setSubmitButtonDisabled(true);
    }

    return valid;
  };

  const handleSubmit = async (mirroredLogin: boolean) => {
    setSubmitButtonLoading(true);

    const inputsAreValid = validateInputs();

    //If inputs aren't valid then don't continue with submit.
    if (!inputsAreValid && !mirroredLogin) {
      setSubmitButtonLoading(false);
      return;
    }

    try {
      let loginResponse = [];

      if (mirroredLogin) {
        loginResponse = await API.loginAsUser(mirrorToken);
      } else {
        loginForm.email = loginForm.email.toLowerCase();
        loginResponse = await API.login(loginForm);
      }

      const [user, error] = loginResponse;
      if (error) {
        console.error(error);
        showNotification("Failed to login!\n" + error.message, NotificationType.ERROR);
        return;
      }

      let seller;
      if (user.role === UserRole.SELLER || user.role === UserRole.ADMIN || user.role === UserRole.SUPPORT) {
        seller = user;
      } else {
        if (seller) {
          const targetSeller = user.sellers.find((s) => s.uuid === seller.uuid);
          if (!targetSeller) {
            // the current seller has been removed
            seller = user.sellers[0];
          } else {
            // refresh the current seller
            seller = targetSeller;
          }
        } else {
          seller = user.sellers[0];
        }
      }
      setUser(user as any, true); // @TODO fix 'any' here....

      //If a user has the ADMIN role then login is a success. DON'T fetch subscription. Admins will NEVER have a subscription.
      if (user.role === UserRole.ADMIN || user.role === UserRole.SUPPORT) {
        showNotification(`Logged in successfully! With great power comes great responsibility.`, NotificationType.SUCCESS);
        setTimeout(() => {
          navigate(navigatePath || "~/admin/users");
        })

        //Don't continue on.
        return;
      }

      if (!user.emailVerified) {
        showNotification("Logged in successfully! Please verify your email before proceeding.", NotificationType.SUCCESS);
        navigate("~/auth/verify-email");

      } else if (user.status === UserStatus.DOING_INITIAL_PAYMENT) {
        showNotification("Logged in successfully! Please complete your payment information to continue to Boxem.", NotificationType.SUCCESS);
        navigatePreserveSearchParams("~/auth/payment-information", navigate);

      } else {
        refetchSubscription().then((subscription) => {
          if (window.LogRocket) {
            // @TODO(michael) we aren't currently getting the user's ID returned here, we should use that for identify instead of email.
            // also, we need to call this on page load if the user is signed in already and somehow get their information.
            window.LogRocket?.identify(seller.email, {
              name: seller.name,
              email: seller.email,
              planCode: subscription?.planCode ?? undefined,
            });
          }
        });
        showNotification("Logged in successfully!", NotificationType.SUCCESS);
        setTimeout(async () => {
          if (walmartCallBackUri && walmartClientType === 'seller') {
            const { url: oauthUrl } = await getWFSAuthLink();
            window.location.href = oauthUrl;
          }
          else {
            // setTimeout seems to be necessary here to avoid a race condition where the dashboard layout checks the value of { user } in 
            // the UserContext. even though we have set it before navigating, it sometimes sees the user as undefined.
            navigate(navigatePath || "~/");
          }
        });
      }
    } catch (err: any) {
      console.error(err);
      showNotification("Failed to login!\nAn unknown error occurred.", NotificationType.ERROR);

    } finally {
      setSubmitButtonDisabled(false);
      setSubmitButtonLoading(false);
    }
  };

  //Handle email change
  const onEmailChange = (email: string) => {
    setLoginForm({...loginForm, email });
    setEmailErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Handle password change
  const onPasswordChange = (password: string) => {
    setLoginForm({...loginForm, password });
    setPasswordErrorText("");
    setSubmitButtonDisabled(false);
  };

  //Cleanup form once comp in unmounted
  useEffect(() => {
    return () => {
      setLoginForm({
        email: "",
        password: "",
      });
    };
  }, []);

  useEffect(() => {
    if (!!mirrorToken) {
      handleSubmit(true);
    } else if (!!autofillEmail && (/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(autofillEmail)) {
      setLoginForm({...loginForm, email: autofillEmail });
    }
  }, []);

  if (!!mirrorToken) {
    return (
      <div className={styles.formContainer}>
        <h3 style={{ display: "block" }}>
          Establishing Mirrored Connection...
        </h3>
        <LoadingSpinner size="3rem" />
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <h3 style={{ display: "block" }}>
        Log in to your account
      </h3>

      <div className={styles.inputGroup}>
        <label htmlFor="email">
          <Flex gap={1.5} align="center">
            <span>Email address</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            type="email"
            inputId="email"
            placeholder="your@email.com"
            autocomplete="username"
            value={loginForm.email}
            onChange={onEmailChange}
            onSubmit={() => handleSubmit(false)}
          />
        </Flex>

        {emailErrorText && <p className={`small-text ${styles.invalidText}`}>{emailErrorText}</p>}
      </div>

      <div className={styles.inputGroup}>
        <label htmlFor="password">
          <Flex gap={1.5} align="center">
            <span>Password</span>
          </Flex>
        </label>

        <Flex
          direction="column"
          gap={2}
          className={styles.inputContainer}
        >
          <TextInput
            className={styles.input}
            placeholder="Your password"
            type="password"
            inputId="password"
            autocomplete="current-password"
            value={loginForm.password}
            onChange={onPasswordChange}
            onSubmit={() => handleSubmit(false)}
          />
        </Flex>

        {passwordErrorText && <p className={`small-text ${styles.invalidText}`}>{passwordErrorText}</p>}
      </div>

      <div>
        <Flex justify="end" style={{ marginBottom: "1rem" }}>
          <Link href="/auth/forgot-password">
            <span>Forgot password?</span>
          </Link>
        </Flex>

        <Button
          type="primary"
          disabled={submitButtonLoading || submitButtonDisabled}
          className={styles.submitButton}
          onClick={() => handleSubmit(false)}
        >
          Log In
        </Button>

        <div className={styles.space}></div>

        <Flex justify="center">
          <span>
            {"Don't have an account?"} <a href="https://www.boxem.com/pricing">Sign up</a>
          </span>
        </Flex>
      </div>
    </div>
  );
}
